import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const refreshMe = useCallback(async () => {
    // Skip if no token AND no cookie (we can't tell about the cookie, so try anyway only if token exists)
    if (!tokenStore.get()) {
      setUser(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      tokenStore.clear();
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      // AuthCallback will set user
      return;
    }
    refreshMe();
  }, [refreshMe]);

  const _applyAuth = (resp) => {
    if (resp?.access_token) tokenStore.set(resp.access_token);
    setUser(resp?.user || null);
    return resp?.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return _applyAuth(data);
  };

  const completeMfa = async (mfa_token, code) => {
    const { data } = await api.post("/auth/login/mfa", { mfa_token, code });
    return _applyAuth(data);
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    return _applyAuth(data);
  };

  const finalizeGoogleSession = (resp) => _applyAuth(resp);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    tokenStore.clear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, completeMfa, register, finalizeGoogleSession, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
